import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import { useCreditCode, rewardView, failInfo, groupCode, whenText, turnPct } from "../creditCodeLogic";
import { Empty, LoginGate, Skeleton } from "../ui";

/*
 * หน้า "เครดิตโค้ด" — ดีไซน์ landing-ver2 (ตู้เซฟนีออน): ช่องรหัสแสดงเป็นช่องตัวอักษรเรืองแสง กดแล้ว "ปลดล็อก" รางวัล
 * (ตรรกะอยู่ที่ creditCodeLogic.js ร่วมกับ landing ซึ่งใช้ดีไซน์ตั๋วคูปองแทน)
 */

const MIN_SLOTS = 8;

/** ตัวเลขวิ่งขึ้นจาก 0 → เป้าหมาย (ปิดอนิเมชันเมื่อผู้ใช้ตั้ง reduce-motion) */
function useCountUp(target, ms = 800) {
  const [v, setV] = useState(0);
  useEffect(() => {
    const reduce = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce || !target) {
      setV(target || 0);
      return undefined;
    }
    const steps = 24;
    let i = 0;
    const id = setInterval(() => {
      i += 1;
      setV(Math.round((target * Math.min(1, i / steps)) * 100) / 100);
      if (i >= steps) clearInterval(id);
    }, ms / steps);
    return () => clearInterval(id);
  }, [target, ms]);
  return v;
}

function Unlocked({ result, onClose }) {
  const r = rewardView(result);
  const shown = useCountUp(r ? r.amount : 0);
  const closeRef = useRef(null);
  useEffect(() => {
    if (!result) return undefined;
    if (closeRef.current) closeRef.current.focus();
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [result, onClose]);
  if (!r) return null;
  const text = r.diamond ? shown.toLocaleString("th-TH") : `฿${shown.toLocaleString("th-TH", { maximumFractionDigits: 2 })}`;
  return (
    <div className="ccv-overlay" role="dialog" aria-modal="true" aria-label="ปลดล็อกโค้ดสำเร็จ" onClick={onClose}>
      <div className="ccv-unlocked" onClick={(e) => e.stopPropagation()}>
        <div className="ccv-rays" aria-hidden="true" />
        <div className="ccv-unlocked-ic">
          <Icon icon={r.diamond ? "solar:diamond-bold-duotone" : "solar:wallet-money-bold-duotone"} width={44} />
        </div>
        <div className="ccv-unlocked-kicker">
          <Icon icon="solar:lock-unlocked-bold" width={16} /> ปลดล็อกสำเร็จ
        </div>
        <div className="ccv-unlocked-amount" aria-hidden="true">
          {text}
          <small>{r.unit}</small>
        </div>
        <span className="ccv-sr">
          ได้รับ {r.big} {r.unit}
        </span>
        {r.detail && <p className="ccv-unlocked-detail">{r.detail}</p>}
        <ul className="ccv-unlocked-notes">
          <li>
            <Icon icon="solar:check-circle-bold" width={16} />
            {r.diamond ? "เพชรเข้าบัญชีของคุณแล้ว" : "เครดิตเข้ากระเป๋าของคุณแล้ว"}
          </li>
          {r.turnover && (
            <li className="warn">
              <Icon icon="solar:lock-keyhole-minimalistic-bold" width={16} />
              {r.turnover}
            </li>
          )}
          {r.scope && (
            <li className="warn">
              <Icon icon="solar:ticket-bold" width={16} />
              {r.scope}
            </li>
          )}
        </ul>
        <button type="button" ref={closeRef} className="ccv-btn" onClick={onClose}>
          เยี่ยมเลย
        </button>
      </div>
    </div>
  );
}

export default function CreditCodePage({ onDone }) {
  const nav = useNavigate();
  const cc = useCreditCode(onDone);
  const inputRef = useRef(null);
  const [focus, setFocus] = useState(false);

  if (!cc.isAuthenticated) {
    return (
      <LoginGate
        icon="solar:lock-keyhole-bold-duotone"
        title="เข้าสู่ระบบเพื่อปลดล็อกโค้ดรางวัล"
        desc="กรอกรหัสโค้ดที่ได้รับจากกิจกรรม เพื่อรับเครดิตหรือเพชรเข้าบัญชี"
        perks={["รับเครดิต/เพชรทันที", "ดูประวัติการกรอกโค้ด", "ดูเงื่อนไขของแต่ละโค้ด"]}
        onLogin={() => nav("/auth")}
      />
    );
  }

  const failed = cc.result && !cc.result.ok ? cc.result : null;
  const info = failed ? failInfo(failed.code) : null;
  const groups = groupCode(cc.code);
  const slotCount = Math.max(MIN_SLOTS, cc.code.length);

  const paste = async () => {
    try {
      cc.setCode(await navigator.clipboard.readText());
    } catch (_) {
      /* เบราว์เซอร์ไม่ให้อ่านคลิปบอร์ด — โฟกัสช่องให้ผู้ใช้วางเอง */
    }
    if (inputRef.current) inputRef.current.focus();
  };

  // ช่องแสดงผล: แบ่งเป็นกลุ่มละ 4 ช่อง (กลุ่มสุดท้ายเติมช่องว่างให้ครบอย่างน้อย MIN_SLOTS)
  const cells = Array.from({ length: Math.ceil(slotCount / 4) * 4 }, (_, i) => cc.code[i] || "");
  const caretAt = Math.min(cc.code.length, cells.length - 1);

  return (
    <div className="ccv-page">
      <section className={`ccv-vault${cc.enabled ? "" : " is-off"}${failed ? " is-fail" : ""}${cc.busy ? " is-busy" : ""}`} aria-label="ตู้เซฟเครดิตโค้ด">
        <div className="ccv-glow" aria-hidden="true" />
        <header className="ccv-top">
          <span className="ccv-ring">
            <Icon icon={cc.busy ? "solar:lock-unlocked-bold-duotone" : "solar:lock-keyhole-bold-duotone"} width={28} />
          </span>
          <div className="ccv-title">
            <h1>เครดิตโค้ด</h1>
            <p>กรอกรหัสลับเพื่อปลดล็อกเครดิต / เพชรฟรี</p>
          </div>
          <span className={`ccv-led ${cc.enabled ? "on" : "off"}`}>{cc.enabled ? "ONLINE" : "ปิดอยู่"}</span>
        </header>

        <form onSubmit={cc.submit} className="ccv-form">
          <div className={`ccv-slots${focus ? " focus" : ""}`} onClick={() => inputRef.current && inputRef.current.focus()}>
            <input
              ref={inputRef}
              className="ccv-input"
              aria-label="รหัสโค้ด"
              value={cc.code}
              onChange={(e) => cc.setCode(e.target.value)}
              onPaste={(e) => {
                e.preventDefault();
                cc.setCode(e.clipboardData.getData("text"));
              }}
              onFocus={() => setFocus(true)}
              onBlur={() => setFocus(false)}
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              maxLength={64}
              disabled={!cc.enabled}
              readOnly={cc.busy} /* ไม่ disabled ระหว่างรอ — ไม่งั้นช่องเสียโฟกัสและพิมพ์แก้ต่อไม่ได้ */
              aria-invalid={!!failed}
            />
            <div className="ccv-cells" aria-hidden="true">
              {cells.map((ch, i) => (
                <React.Fragment key={i}>
                  {i > 0 && i % 4 === 0 && <span className="ccv-dash">–</span>}
                  <span className={`ccv-cell${ch ? " filled" : ""}${focus && i === caretAt ? " caret" : ""}`}>{ch}</span>
                </React.Fragment>
              ))}
            </div>
          </div>

          <div className="ccv-tools">
            <button type="button" className="ccv-chip" onClick={paste} disabled={!cc.enabled || cc.busy}>
              <Icon icon="solar:clipboard-bold-duotone" width={16} /> วางรหัส
            </button>
            {groups.length > 0 && (
              <button type="button" className="ccv-chip ghost" onClick={() => { cc.setCode(""); if (inputRef.current) inputRef.current.focus(); }} disabled={cc.busy}>
                <Icon icon="solar:eraser-bold-duotone" width={16} /> ล้าง
              </button>
            )}
            <span className="ccv-len">{cc.code.length}/64</span>
          </div>

          {failed && (
            <div className="ccv-fail" role="alert">
              <Icon icon="solar:shield-warning-bold" width={20} />
              <div>
                <b>{failed.message || info.title}</b>
                {info.hint && <span>{info.hint}</span>}
              </div>
            </div>
          )}

          {cc.enabled ? (
            <button type="submit" className="ccv-btn" disabled={cc.busy || !cc.code}>
              <Icon icon={cc.busy ? "svg-spinners:ring-resize" : "solar:lock-unlocked-bold"} width={20} />
              {cc.busy ? "กำลังปลดล็อก..." : "ปลดล็อกรางวัล"}
            </button>
          ) : (
            <div className="ccv-closed">
              <Icon icon="solar:lock-keyhole-minimalistic-bold" width={18} /> ระบบเครดิตโค้ดปิดให้บริการชั่วคราว
            </div>
          )}
        </form>
      </section>

      {cc.active.length > 0 && (
        <section className="ccv-sec">
          <h2>
            <Icon icon="solar:shield-keyhole-bold-duotone" width={20} /> เครดิตที่ยังมีเงื่อนไข
          </h2>
          <div className="ccv-actives">
            {cc.active.map((a) => (
              <article className="ccv-active" key={a.id}>
                <div className="ccv-meter" style={{ "--p": `${a.locked ? turnPct(a) : 100}%` }} aria-hidden="true">
                  <b>{a.locked ? `${Math.round(turnPct(a))}%` : <Icon icon="solar:ticket-bold" width={18} />}</b>
                </div>
                <div className="ccv-active-body">
                  <div className="ccv-active-top">
                    <code>{a.code}</code>
                    <strong>฿{a.amount.toLocaleString("th-TH")}</strong>
                  </div>
                  {a.locked && (
                    <span>
                      ทำเทิร์นแล้ว ฿{a.turnover_done.toLocaleString("th-TH")} จาก ฿{a.turnover_required.toLocaleString("th-TH")}
                    </span>
                  )}
                  {a.restricted_left > 0 && (
                    <span>
                      เหลือ ฿{a.restricted_left.toLocaleString("th-TH")} · {a.play_label}
                    </span>
                  )}
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      <section className="ccv-sec">
        <h2>
          <Icon icon="solar:history-bold-duotone" width={20} /> ประวัติการกรอก
        </h2>
        {cc.loading ? (
          <Skeleton h={120} />
        ) : cc.history.length === 0 ? (
          <div className="lx-card">
            <Empty icon="solar:lock-keyhole-bold-duotone">ยังไม่เคยกรอกโค้ด</Empty>
          </div>
        ) : (
          <ol className="ccv-timeline">
            {cc.history.map((h) => (
              <li key={h.id} className={h.ok ? "ok" : "err"}>
                <span className="ccv-dot">
                  <Icon icon={h.ok ? "solar:check-read-bold" : "solar:close-circle-bold"} width={14} />
                </span>
                <div className="ccv-tl-body">
                  <b>{h.title}</b>
                  <span>
                    <code>{h.code}</code> · {whenText(h.at)}
                  </span>
                </div>
                {h.ok && <strong className="ccv-tl-amt">{h.amountText}</strong>}
              </li>
            ))}
          </ol>
        )}
      </section>

      <Unlocked result={cc.result && cc.result.ok ? cc.result : null} onClose={cc.closeResult} />
    </div>
  );
}
